export type SupplierLocation = {
  locationId: string;
  address: string;
  type: string;
  line1: string;
  line2: string;
  city: string;
  county: string;
  country: string;
  postcode: string;
  coordinates: {
    lon: number;
    lat: number;
  };
  isPrimary: boolean;
  isActive: boolean;
};

export type SupplierLegalEntity = {
  legalName: string;
  tradingName: string;
  companyRegistrationNumber: string;
  vatNumber: string;
  taxId: string;
  jurisdictionCountry: string;
  legalEntityType: string;
  incorporationDate: string;
};

export type SupplierContact = {
  contactId: string;
  name: string;
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  role: string;
  locationId: string;
  isActive: boolean;
};

export type SupplierCorporateHierarchy = {
  parentSupplierId: string;
  ultimateParentSupplierId: string;
  hierarchyLevel: string;
  regionalGrouping: string;
};

export type SupplierCertification = {
  name: string;
  issuingBody: string;
  certificateNumber: string;
  issueDate: string;
  expiryDate: string;
  status: string;
  documentUrl: string;
};

export type SupplierInsurancePolicy = {
  type: string;
  provider: string;
  policyNumber: string;
  coverageAmount: string;
  currency: string;
  expiryDate: string;
  documentUrl: string;
};

export type SupplierCompliance = {
  certifications: SupplierCertification[];
  insurancePolicies: SupplierInsurancePolicy[];
  esgRating: string;
  esgAssessmentDate: string;
  sanctionsCheckStatus: string;
  sanctionsCheckedAt: string;
};

export type SupplierSkuLink = {
  inventorySkuId: string;
  skuCode: string;
  productName: string;
  supplierSkuCode: string;
  leadTimeMeanDays: string;
  leadTimeStdDays: string;
  minimumOrderQuantity: string;
  orderMultiple: string;
  orderMultipleUnit: string;
  incoterms: string;
  isPreferred: boolean;
  status: string;
};

export type SupplierFormValues = {
  supplierId: string;
  name: string;
  type: string;
  contactName: string;
  contactEmail: string;
  contactNumber: string;
  hqId?: string;
  hqName?: string;
  locations: SupplierLocation[];
  legalEntity: SupplierLegalEntity;
  contacts: SupplierContact[];
  corporateHierarchy: SupplierCorporateHierarchy;
  compliance: SupplierCompliance;
  suppliedSkus: SupplierSkuLink[];
  sourceType: string;
  brandsSupplied: string;
  coverageBranches: string;
  certificationStatus: string;
  certificationLabel: string;
  certificationExpiry: string;
  lifecycleStatus: string;
  readinessGaps: string;
};

export const emptySupplierClassification = {
  sourceType: '',
  brandsSupplied: '',
  coverageBranches: '',
  certificationStatus: '',
  certificationLabel: '',
  certificationExpiry: '',
  lifecycleStatus: '',
  readinessGaps: '',
};

export const emptySupplierLegalEntity: SupplierLegalEntity = {
  legalName: '',
  tradingName: '',
  companyRegistrationNumber: '',
  vatNumber: '',
  taxId: '',
  jurisdictionCountry: '',
  legalEntityType: '',
  incorporationDate: '',
};

export const emptySupplierContact: SupplierContact = {
  contactId: '',
  name: '',
  email: '',
  phoneCountryCode: '',
  phoneNumber: '',
  role: '',
  locationId: '',
  isActive: true,
};

export const emptySupplierCorporateHierarchy: SupplierCorporateHierarchy = {
  parentSupplierId: '',
  ultimateParentSupplierId: '',
  hierarchyLevel: '',
  regionalGrouping: '',
};

export const emptySupplierCertification: SupplierCertification = {
  name: '',
  issuingBody: '',
  certificateNumber: '',
  issueDate: '',
  expiryDate: '',
  status: '',
  documentUrl: '',
};

export const emptySupplierInsurancePolicy: SupplierInsurancePolicy = {
  type: '',
  provider: '',
  policyNumber: '',
  coverageAmount: '',
  currency: '',
  expiryDate: '',
  documentUrl: '',
};

export const emptySupplierCompliance: SupplierCompliance = {
  certifications: [],
  insurancePolicies: [],
  esgRating: '',
  esgAssessmentDate: '',
  sanctionsCheckStatus: '',
  sanctionsCheckedAt: '',
};

export const emptySupplierSkuLink: SupplierSkuLink = {
  inventorySkuId: '',
  skuCode: '',
  productName: '',
  supplierSkuCode: '',
  leadTimeMeanDays: '',
  leadTimeStdDays: '',
  minimumOrderQuantity: '',
  orderMultiple: '',
  orderMultipleUnit: '',
  incoterms: '',
  isPreferred: false,
  status: '',
};
